import {
	AppId,
	NavigationIntent,
	OrganizationType,
	type RouteDefinition,
	type RouteParams
} from './types';

const requireParam = (params: RouteParams, key: keyof RouteParams): string => {
	const value = params[key];

	if (typeof value !== 'string' || value.length === 0) {
		throw new Error(`Missing route param: ${key}`);
	}

	return value;
};

const pathParam = (params: RouteParams, key: keyof RouteParams) =>
	encodeURIComponent(requireParam(params, key));

const requireOrganizationParams = (
	params: RouteParams,
	expectedType?: OrganizationType
) => {
	const organizationIdn = requireParam(params, 'organizationIdn');
	const organizationType = params.organizationType;

	if (!organizationType) {
		throw new Error('Missing route param: organizationType');
	}

	if (expectedType && organizationType !== expectedType) {
		throw new Error(`Expected ${expectedType} organization route params`);
	}

	return { organizationIdn, organizationType };
};

const scopedPath = (
	params: RouteParams,
	expectedType: OrganizationType,
	suffix: string
) => {
	requireOrganizationParams(params, expectedType);

	return `/${expectedType}/${pathParam(params, 'organizationIdn')}/${suffix}`;
};

const creatorPath = (params: RouteParams) => {
	if (!params.organizationIdn && !params.organizationType) {
		return '/agent-creator';
	}

	const { organizationType } = requireOrganizationParams(params);

	return `/${organizationType}/${pathParam(params, 'organizationIdn')}/agent-creator`;
};

/**
 * Central routing registry used by the routing client.
 *
 * Each route definition describes:
 * - `intent`     : navigation intent exposed to consumers
 * - `appId`      : application responsible for rendering the route
 * - `buildPath`  : intent -> URL transformation
 * - `matchPaths` : URL -> intent transformation
 *
 * Adding a new route:
 *
 * 1. Add a new NavigationIntent.
 * 2. Register a RouteDefinition in this catalog.
 * 3. Prefer `scopedPath(...)` for organization-scoped routes.
 * 4. Add corresponding routing tests to ensure:
 *    - intent -> URL resolution
 *    - URL -> intent resolution
 *    - round-trip consistency
 *
 * Example:
 *
 * {
 *   appId: AppId.Portal,
 *   intent: NavigationIntent.CustomerReports,
 *   buildPath: (params) =>
 *     scopedPath(params, OrganizationType.Customer, 'reports'),
 *   matchPaths: ['/customer/:organizationIdn/reports']
 * }
 */

export const routeCatalog: readonly RouteDefinition[] = [
	{
		appId: AppId.Portal,
		intent: NavigationIntent.AccountRoot,
		buildPath: () => '/',
		matchPaths: ['/']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.PartnerSignup,
		buildPath: () => '/partner-signup',
		matchPaths: ['/partner-signup']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.AuthLogin,
		buildPath: () => '/auth/login',
		matchPaths: ['/auth/login']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.AuthCallback,
		buildPath: () => '/auth/login/callback',
		matchPaths: ['/auth/login/callback']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.AuthOAuth,
		buildPath: () => '/auth/oauth',
		matchPaths: ['/auth/oauth']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.AuthSsoCallback,
		buildPath: () => '/auth/sso-auth-callback',
		matchPaths: ['/auth/sso-auth-callback']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.CustomerDashboard,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Customer, 'dashboard'),
		matchPaths: ['/customer/:organizationIdn/dashboard']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.CustomerContacts,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Customer, 'contacts'),
		matchPaths: ['/customer/:organizationIdn/contacts']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.CustomerSessions,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Customer, 'sessions'),
		matchPaths: ['/customer/:organizationIdn/sessions']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.CustomerSessionDetails,
		buildPath: (params) =>
			`${scopedPath(params, OrganizationType.Customer, 'sessions')}/${pathParam(params, 'sessionId')}`,
		matchPaths: ['/customer/:organizationIdn/sessions/:sessionId']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.CustomerRecording,
		buildPath: (params) =>
			`${scopedPath(params, OrganizationType.Customer, 'recording')}/${pathParam(params, 'recordId')}`,
		matchPaths: ['/customer/:organizationIdn/recording/:recordId']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.CustomerSettings,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Customer, 'settings'),
		matchPaths: ['/customer/:organizationIdn/settings']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.CustomerTeam,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Customer, 'team'),
		matchPaths: ['/customer/:organizationIdn/team']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.CustomerProfile,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Customer, 'profile'),
		matchPaths: ['/customer/:organizationIdn/profile']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.CustomerBilling,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Customer, 'billing'),
		matchPaths: ['/customer/:organizationIdn/billing']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.CustomerWidgets,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Customer, 'widgets'),
		matchPaths: ['/customer/:organizationIdn/widgets']
	},
	{
		appId: AppId.VibeBuilder,
		intent: NavigationIntent.CustomerVibeBuilder,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Customer, 'vibe-builder'),
		matchPaths: ['/customer/:organizationIdn/vibe-builder']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.CustomerAgentConfiguration,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Customer, 'agent-configuration'),
		matchPaths: ['/customer/:organizationIdn/agent-configuration']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.PartnerDashboard,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Partner, 'dashboard'),
		matchPaths: ['/partner/:organizationIdn/dashboard']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.PartnerCustomers,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Partner, 'customers'),
		matchPaths: ['/partner/:organizationIdn/customers']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.PartnerEmployees,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Partner, 'employees'),
		matchPaths: ['/partner/:organizationIdn/employees']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.PartnerContracts,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Partner, 'contracts'),
		matchPaths: ['/partner/:organizationIdn/contracts']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.PartnerTransactions,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Partner, 'transactions'),
		matchPaths: ['/partner/:organizationIdn/transactions']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.PartnerTeam,
		buildPath: (params) => scopedPath(params, OrganizationType.Partner, 'team'),
		matchPaths: ['/partner/:organizationIdn/team']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.PartnerProfile,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Partner, 'profile'),
		matchPaths: ['/partner/:organizationIdn/profile']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.PartnerBilling,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Partner, 'billing'),
		matchPaths: ['/partner/:organizationIdn/billing']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.PartnerMembers,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Partner, 'partner-members'),
		matchPaths: ['/partner/:organizationIdn/partner-members']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.PartnerProducts,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Partner, 'products'),
		matchPaths: ['/partner/:organizationIdn/products']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.PartnerCustomerQuotas,
		buildPath: (params) =>
			scopedPath(params, OrganizationType.Partner, 'customer-quotas'),
		matchPaths: ['/partner/:organizationIdn/customer-quotas']
	},
	{
		appId: AppId.Creator,
		intent: NavigationIntent.CreatorAgentCreation,
		buildPath: creatorPath,
		matchPaths: [
			'/agent-creator',
			'/customer/:organizationIdn/agent-creator',
			'/partner/:organizationIdn/agent-creator'
		],
		requiresAuth: true
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.PublicSessionDetails,
		buildPath: (params) => `/sessions/${pathParam(params, 'sessionId')}`,
		matchPaths: ['/sessions/:sessionId']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.PublicRecording,
		buildPath: (params) => `/recording/${pathParam(params, 'recordId')}`,
		matchPaths: ['/recording/:recordId']
	},
	{
		appId: AppId.Portal,
		intent: NavigationIntent.ScopedNotFound,
		matchPaths: ['/customer/:organizationIdn/*', '/partner/:organizationIdn/*']
	}
] as const;
